import { AnalysisRequest } from '../types/data/bedrock';
import { getKPIName } from '../utils/kpiMapping';

// API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const BEDROCK_ENDPOINTS = {
  analyze: `${API_BASE_URL}/bedrock/analyze`,
  analyzeFile: `${API_BASE_URL}/bedrock/analyze/file`,
  analyzeData: `${API_BASE_URL}/bedrock/analyze/data`,
  models: `${API_BASE_URL}/bedrock/models`
};

// Types
export interface BedrockModel {
  id: string;
  name: string;
}

// Validation rule generation request type
export interface ValidationRuleGenerationRequest {
  failureRate: number;
  numberOfRules: number;
  filteredData: {
    records: any[];
    kpiDetails: { [code: string]: { category: string; values: number[] } };
    brands: string[];
    totalRecords: number;
  };
  modelId?: string;
  existingWeight?: number;
  availableWeight?: number;
}

// Get available models
export async function getAvailableModels(): Promise<BedrockModel[]> {
  try {
    const response = await fetch(BEDROCK_ENDPOINTS.models);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.models;
  } catch (error) {
    console.error('Error fetching available models:', error);
    throw error;
  }
}

// Generate AI validation rules
export async function generateValidationRules(request: ValidationRuleGenerationRequest): Promise<any[]> {
  try {
    const allKPIs = Object.keys(request.filteredData.kpiDetails);
    const kpiCodes = Object.keys(request.filteredData.kpiDetails).sort();
    const kpiNames = kpiCodes.map(code => getKPIName(code));
    const kpiCodeToNameMap = Object.fromEntries(kpiCodes.map(code => [code, getKPIName(code)]));
    
    // Create the prompt for validation rule generation focused on DATA QUALITY
    const prompt = `You are a data quality expert for automotive dealership financial systems. 

TASK: Analyze the attached CSV data file and generate exactly ${request.numberOfRules} DATA QUALITY VALIDATION RULES that will identify ${request.failureRate}% of the records as having data quality issues.

CRITICAL INSTRUCTION: You MUST use ONLY the exact KPI NAMES that appear as column headers in the CSV file. DO NOT use KPI codes - use the full descriptive names.

CSV DATA STRUCTURE:
The CSV file contains automotive dealership financial data with these columns:
- Brand: Dealership brand name (EXTRACT ACTUAL VALUES FROM THIS COLUMN)
- Year: Data year  
- Month: Data month
- Then one column for each KPI name: ${kpiNames.join(', ')}

IMPORTANT: The KPI NAMES are the COLUMN HEADERS in the CSV (not values in a column). Each KPI name like "Total Units Sold" has its own column with the values.

BRAND HANDLING:
The CSV contains data for: ${request.filteredData.brands.join(', ')}
You do NOT need to specify brands in your rules - we will handle brand assignment automatically.

CRITICAL DATA INTERPRETATION:
- ONLY analyze NUMERIC values in the KPI columns
- IGNORE any short alphanumeric codes that look like "MP8E", "00ZK", "F8US" - these are NOT meaningful data
- Focus on actual numbers like 32, 1756199, 131779, etc.
- Do NOT create rules about "encoded data" or "missing codes" - only use real numeric values
- If a KPI column has mostly empty or non-numeric values, create rules about missing data, not encoded data

STEP-BY-STEP PROCESS:
1. FIRST: Look at the CSV column headers - each KPI code is a separate column
2. SECOND: Use ONLY the column header names as KPI codes in your validation rules  
3. THIRD: Create rules based on the actual values in each KPI column
4. FOURTH: Leave the "brand" field empty ("") - we will assign the correct brand automatically

SPECIFIC KPI ANALYSIS REQUIRED:
Based on the data provided, here are the KPIs you should focus on:

${allKPIs.map(code => {
  const kpiName = getKPIName(code);
  const values = request.filteredData.kpiDetails[code].values;
  if (values.length === 0) return `${kpiName} (${request.filteredData.kpiDetails[code].category}): NO DATA - Create rule to flag missing values`;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const sortedValues = [...values].sort((a, b) => a - b);
  const median = sortedValues[Math.floor(sortedValues.length / 2)];
  const stdDev = Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / values.length);
  
  // Calculate outlier thresholds
  const lowerOutlier = avg - (2 * stdDev);
  const upperOutlier = avg + (2 * stdDev);
  
  return `${kpiName} (${request.filteredData.kpiDetails[code].category}):
   - Range: ${min} to ${max}
   - Average: ${avg.toFixed(2)}, Median: ${median.toFixed(2)}
   - Standard Deviation: ${stdDev.toFixed(2)}
   - Outlier Thresholds: < ${lowerOutlier.toFixed(2)} or > ${upperOutlier.toFixed(2)}
   - Sample Count: ${values.length} records`;
}).join('\n\n')}

KPI RELATIONSHIP ANALYSIS - CREATE CONNECTED VALIDATION RULES:
You must analyze the KPIs and create rules that validate business logic relationships between related KPIs. Look for these types of connections:

1. **REVENUE-VOLUME RELATIONSHIPS**: 
   - If UNITS_SOLD = 0, then REVENUE_TOTAL should be 0
   - If SERVICE_HOURS = 0, then SERVICE_REVENUE should be 0
   - REVENUE_TOTAL should generally correlate with UNITS_SOLD

2. **PROFIT-REVENUE RELATIONSHIPS**:
   - GROSS_PROFIT should never exceed REVENUE_TOTAL
   - If REVENUE_TOTAL = 0, then GROSS_PROFIT should be 0
   - GROSS_PROFIT should not be negative (unless legitimate loss)

3. **EFFICIENCY RELATIONSHIPS**:
   - CONVERSION_RATE should be between 0-100%
   - If LEADS = 0, then CONVERSION_RATE should be 0
   - AVG_SALE_PRICE * UNITS_SOLD should approximately equal REVENUE_TOTAL

4. **CUSTOMER RELATIONSHIPS**:
   - CUST_SATISFACTION scores should be within valid ranges (e.g., 1-10)
   - NET_PROMOTER scores should be within valid ranges (e.g., -100 to +100)
   - REPEAT_CUSTOMERS should not exceed total customers

5. **TEMPORAL CONSISTENCY**:
   - Monthly values should be reasonable for the time period
   - Seasonal patterns should make business sense

CONNECTED RULE EXAMPLES (adapt to your actual KPI data):
- "When UNITS_SOLD is 0, REVENUE_TOTAL must also be 0 - indicates data inconsistency"
- "GROSS_PROFIT exceeding REVENUE_TOTAL indicates calculation error"
- "SERVICE_REVENUE without corresponding SERVICE_HOURS suggests missing data"
- "CONVERSION_RATE above 100% is mathematically impossible"
- "CUST_SATISFACTION scores outside 1-10 range indicate invalid data entry"

CRITICAL: Create both SINGLE-KPI rules (outlier detection) AND MULTI-KPI rules (relationship validation).

RULE CREATION INSTRUCTIONS:
1. **ANALYZE THE CSV DATA**: Look at the actual values in the CSV file for each KPI Code
2. **CREATE SPECIFIC RULES**: Use the exact KPI codes and realistic thresholds based on the data
3. **TARGET ${request.failureRate}% FAILURE RATE**: Create rules that will flag approximately ${request.failureRate}% of the records
4. **FOCUS ON DATA QUALITY**: Rules should identify outliers, impossible values, missing data, and business logic violations

CRITICAL: RULES MUST BE AUTOMATICALLY SCOREABLE WITH EXPLICIT FAILURE CONDITIONS
Your rules will be used later to automatically score new data. Each rule MUST be written so an AI can easily determine if a record passes or fails the rule. 

**RULE FAILURE CRITERIA - BE EXTREMELY EXPLICIT:**
Every rule MUST clearly define when a record FAILS the rule. The rule text should make it obvious what constitutes a failure.

**PERFECT RULE EXAMPLES (EXTREMELY EXPLICIT failure conditions in the rule text itself):**
- "DAYS_IN_INVENTORY values above 50 will fail this rule"
- "UNITS_SOLD values below 10 or above 150 will fail this rule"
- "GROSS_PROFIT exceeding REVENUE_TOTAL by any amount will fail this rule"
- "CONVERSION_RATE above 100% or below 0% will fail this rule"
- "CUST_SATISFACTION scores below 1.0 or above 10.0 will fail this rule"
- "SERVICE_REVENUE greater than 0 when SERVICE_HOURS equals 0 will fail this rule"

**RULE WORDING TEMPLATES - USE THESE EXACT FORMATS WITH EXPLICIT FAILURE STATEMENTS:**
For single KPI threshold rules:
- "KPI_CODE values above X will fail this rule"
- "KPI_CODE values below X will fail this rule"  
- "KPI_CODE values below X or above Y will fail this rule"
- "KPI_CODE values outside the range X to Y will fail this rule"

For relationship rules:
- "KPI_A exceeding KPI_B by any amount will fail this rule"
- "KPI_A greater than 0 when KPI_B equals 0 will fail this rule"
- "KPI_A equals 0 while KPI_B is greater than 0 will fail this rule"

**BAD RULE EXAMPLES (do NOT create these types):**
- "UNITS_SOLD values coded as 'MP8E' indicate missing data" (wrong - ignore codes)
- "Revenue seems unusual" (subjective, no clear failure condition)
- "Data contains encoded values" (wrong - focus on numbers only)
- "Values coded as 'F8US' indicate issues" (wrong - these are not meaningful)
- "High inventory levels" (vague - what is "high"?)
- "Poor performance indicators" (subjective - no specific threshold)

MANDATORY KPI NAMES TO USE (these are the ONLY names allowed):
${kpiNames.join(', ')}

EXAMPLE RULE FORMATS using the actual KPI names above:

SINGLE-KPI RULES (with explicit "will fail this rule" statements):
- "Total Units Sold values below 10 or above 100 will fail this rule"
- "Total Revenue values above 5000000 will fail this rule"
- "Days to Turn Inventory values above 50 will fail this rule"
- "Customer Satisfaction Score values below 1.0 or above 10.0 will fail this rule"

MULTI-KPI RELATIONSHIP RULES (with explicit "will fail this rule" statements):
- "Gross Profit exceeding Total Revenue by any amount will fail this rule"
- "Total Units Sold equals 0 while Total Revenue is greater than 0 will fail this rule"
- "Service Revenue greater than 0 while Labor Hours Sold equals 0 will fail this rule"
- "Lead Conversion Rate above 100% or below 0% will fail this rule"

RULE WORDING REQUIREMENTS - MANDATORY FORMAT:
- **ALWAYS specify exact numeric thresholds** (never use "unusual", "potential", "high", "low")
- **Use precise comparison operators**: "above", "below", "equals", "exceeds", "greater than", "less than"
- **For single thresholds**: "KPI_CODE values above X" or "KPI_CODE values below X"
- **For ranges**: "KPI_CODE values below X or above Y" or "KPI_CODE values outside the range X to Y"
- **For relationships**: use "while", "when", or "and" to connect conditions clearly
- **NEVER use subjective words**: avoid "indicates", "suggests", "potential", "unusual", "seems", "appears"
- **ALWAYS make failure conditions crystal clear**: the rule should read like "This record FAILS if [specific condition]"

**CRITICAL SUCCESS/FAILURE CLARITY:**
Every rule MUST explicitly state "will fail this rule" and the exact numeric conditions:
- ✅ GOOD: "DAYS_IN_INVENTORY values above 50 will fail this rule" (crystal clear)
- ❌ BAD: "DAYS_IN_INVENTORY values above 50" (missing explicit failure statement)
- ❌ BAD: "High DAYS_IN_INVENTORY values" (vague and no failure statement)
- ✅ GOOD: "GROSS_PROFIT exceeding REVENUE_TOTAL by any amount will fail this rule" (crystal clear)
- ❌ BAD: "Profit margins seem unrealistic" (vague and no failure statement)

YOU MUST CREATE A MIX OF BOTH SINGLE-KPI AND MULTI-KPI RULES.
YOU MUST USE THESE EXACT KPI CODES: ${allKPIs.join(', ')}

BRANDS IN DATA: ${request.filteredData.brands.join(', ')}
TOTAL RECORDS TO ANALYZE: ${request.filteredData.totalRecords}

CRITICAL: You must create rules using the EXACT KPI codes found in the CSV data. Look at the "KPI Code" column values.

THE EXACT KPI CODES YOU MUST USE (from the CSV data):
${allKPIs.map(code => `- ${code}`).join('\n')}

DO NOT CREATE FAKE KPI CODES! Only use the codes listed above that appear in the CSV file.

**FINAL RULE VALIDATION CHECKLIST:**
Before creating each rule, verify:
1. ✅ Does the rule specify an EXACT numeric threshold or condition?
2. ✅ Is it crystal clear when a record would FAIL this rule?
3. ✅ Would another AI be able to evaluate this rule consistently?
4. ✅ Does the rule use only approved KPI codes from the list above?
5. ✅ Is the failure condition mathematically precise (no subjective terms)?

**EXAMPLE OF PERFECT RULE CLARITY:**
- Rule: "DAYS_IN_INVENTORY values above 50 will fail this rule"
- Failure condition: Explicitly stated in the rule text itself
- Success condition: Any DAYS_IN_INVENTORY ≤ 50 passes
- Clear? YES - the rule text itself explains when it fails

RETURN FORMAT - EXACT DATABASE STRUCTURE:
Return ONLY a JSON array with exactly this structure (no other text, explanations, or formatting):

[
  {
    "ruleText": "Specific rule using actual KPI names from the CSV data",
    "brand": "Exact brand name from CSV or empty string for all brands", 
    "weight": integer_between_1_and_10,
    "kpiCodes": ["ACTUAL_KPI_NAME_FROM_CSV", "ANOTHER_KPI_NAME_FROM_CSV"]
  }
]

VALIDATION REQUIREMENTS:
- Use ONLY these exact KPI names: ${kpiNames.join(', ')}
- DO NOT create or invent any KPI names not in this list
- Create rules with thresholds that will flag approximately ${request.failureRate}% of the actual records
- Reference specific brands that appear in the CSV data: ${request.filteredData.brands.join(', ')}
- Make rules realistic for automotive dealership financial data

EXAMPLE using the actual KPI names from your data (weights must sum to 100):
[
  {
    "ruleText": "Gross Profit exceeding Total Revenue by any amount will fail this rule",
    "brand": "",
    "weight": 12,
    "kpiCodes": ["Gross Profit", "Total Revenue"]
  },
  {
    "ruleText": "Total Units Sold equals 0 while Total Revenue is greater than 0 will fail this rule", 
    "brand": "",
    "weight": 10,
    "kpiCodes": ["Total Units Sold", "Total Revenue"]
  },
  {
    "ruleText": "Total Units Sold values below 20 or above 80 will fail this rule",
    "brand": "", 
    "weight": 6,
    "kpiCodes": ["Total Units Sold"]
  }
]
Note: Leave all "brand" fields empty - we will automatically assign the correct brand (${request.filteredData.brands.join(', ')}) from your data. For ${request.numberOfRules} rules, ensure all weights sum to exactly 100.

RULE DISTRIBUTION REQUIREMENT:
- Create approximately 60% SINGLE-KPI rules (outlier detection)
- Create approximately 40% MULTI-KPI rules (relationship validation)
- Multi-KPI rules should include 2-3 related KPI codes in the "kpiCodes" array
- Focus on logical business relationships that make sense for automotive dealerships

WEIGHT ASSIGNMENT - CRITICAL REQUIREMENT:
${request.existingWeight ? 
  `IMPORTANT: This user already has validation rules with total weight ${request.existingWeight}/100 for this brand. 
  The weights of your NEW ${request.numberOfRules} rules MUST add up to exactly ${request.availableWeight || (100 - (request.existingWeight || 0))} (not 100).
  Available weight capacity: ${request.availableWeight || (100 - (request.existingWeight || 0))} points.` :
  `The weights of ALL ${request.numberOfRules} rules MUST add up to exactly 100.`
} Assign weights based on importance:

WEIGHT PRIORITIES (higher weight = more important for data quality):
1. **CRITICAL BUSINESS LOGIC (Weight: 8-12 each)**:
   - Revenue/profit mathematical relationships (e.g., profit > revenue)
   - Zero-correlation rules (e.g., 0 units sold but revenue exists)
   - Impossible values (e.g., conversion rate > 100%)

2. **IMPORTANT RANGE VALIDATIONS (Weight: 6-9 each)**:
   - Customer satisfaction score ranges
   - Financial outliers (extreme revenue/profit values)
   - Efficiency metrics outside normal ranges

3. **MODERATE OUTLIER DETECTION (Weight: 4-7 each)**:
   - Statistical outliers (>2-3 standard deviations)
   - Seasonal inconsistencies
   - Volume-based outliers

4. **MINOR DATA QUALITY CHECKS (Weight: 2-5 each)**:
   - Missing data flags
   - Minor inconsistencies
   - Less critical statistical variations

WEIGHT CALCULATION EXAMPLE for ${request.numberOfRules} rules:
${request.existingWeight ? 
  `- Your rules must sum to exactly ${request.availableWeight || (100 - (request.existingWeight || 0))} points (not 100)
  - If generating ${request.numberOfRules} rules with ${request.availableWeight || (100 - (request.existingWeight || 0))} available: distribute proportionally
  - Example: If you have ${request.availableWeight || (100 - (request.existingWeight || 0))} points for ${request.numberOfRules} rules, average would be ${Math.floor((request.availableWeight || (100 - (request.existingWeight || 0))) / request.numberOfRules)} per rule` :
  `- If generating 10 rules: weights might be [12, 11, 10, 9, 9, 8, 8, 7, 6, 20] = 100
  - If generating 20 rules: distribute more evenly, e.g., [8, 7, 6, 6, 5, 5, 5, 4, 4, 4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 27] = 100`
}

CRITICAL: Calculate weights so they sum to exactly ${request.existingWeight ? (request.availableWeight || (100 - (request.existingWeight || 0))) : 100}. Assign higher weights to more critical data quality issues.

FINAL VALIDATION CHECKLIST:
1. Every KPI name in your "kpiCodes" arrays MUST be from this list: ${kpiNames.join(', ')}
2. ALL brand fields should be empty ("") - we handle brand assignment automatically
3. You must generate exactly ${request.numberOfRules} rules
4. The weights of all ${request.numberOfRules} rules MUST sum to exactly ${request.existingWeight ? (request.availableWeight || (100 - (request.existingWeight || 0))) : 100}
5. Include both single-KPI and multi-KPI relationship rules
6. Assign higher weights to more critical data quality issues

WEIGHT VERIFICATION: Before submitting, add up all your weight values to ensure they equal ${request.existingWeight ? (request.availableWeight || (100 - (request.existingWeight || 0))) : 100}.`;

    // Step 1: Save filtered data as CSV file
    const saveFilteredDataAsFile = async (content: string): Promise<string> => {
      const response = await fetch(`${API_BASE_URL}/bedrock/save-filtered-data`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          content,
          debugFileName: 'validation-rule-generation.csv'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save filtered data');
      }

      const data = await response.json();
      return data.fileRef;
    };

    // Prepare CSV content - use SIMPLE format without compression to avoid AI confusion
    // Use KPI NAMES as column headers instead of codes to make AI generate name-based rules
    const headers = ['Brand', 'Year', 'Month'];
    
    // Add each KPI NAME as a separate column
    headers.push(...kpiNames);
    
    console.log('Using KPI names as column headers:', kpiNames);
    console.log('KPI code to name mapping:', kpiCodeToNameMap);
    
    const csvRows = request.filteredData.records.slice(0, 100).map(record => {
      const row = [
        record.brand || '',
        record.year?.toString() || '',
        record.month?.toString() || ''
      ];
      
      // Add value for each KPI (empty if not present in this record)
      kpiCodes.forEach(kpiCode => {
        const kpi = record.kpis?.find((k: any) => k.code === kpiCode);
        row.push(kpi?.value?.toString() || '');
      });
      
      return row;
    });

    // Convert to CSV format
    const csvContent = [headers, ...csvRows]
      .map(row => row.map((cell: any) => `"${cell}"`).join(','))
      .join('\n');

    // Debug: Log the CSV content to see what we're actually sending
    console.log('=== CSV CONTENT BEING SENT TO AI ===');
    console.log('First 15 lines of CSV:');
    console.log(csvContent.split('\n').slice(0, 15).join('\n'));
    console.log('=== END CSV PREVIEW ===');
    
    // Debug: Log the KPI codes we expect to see
    console.log('=== EXPECTED KPI CODES ===');
    console.log('KPI codes from filteredData.kpiDetails:', allKPIs);
    console.log('=== END KPI CODES ===');
    
    // Debug: Check for any weird values in the CSV
    const csvLines = csvContent.split('\n');
    const suspiciousValues = new Set();
    csvLines.slice(1, 20).forEach(line => {
      const values = line.split(',').map(v => v.replace(/"/g, ''));
      values.forEach(value => {
        if (value && value.length < 10 && /^[A-Z0-9]{3,6}$/.test(value) && !allKPIs.includes(value)) {
          suspiciousValues.add(value);
        }
      });
    });
    
    if (suspiciousValues.size > 0) {
      console.log('=== SUSPICIOUS VALUES DETECTED ===');
      console.log('These look like encoded values:', Array.from(suspiciousValues));
      console.log('=== END SUSPICIOUS VALUES ===');
    }

    // Save the CSV content and get fileRef
    const fileRef = await saveFilteredDataAsFile(csvContent);

    // Step 2: Use the file API with the fileRef
    const response = await fetch(BEDROCK_ENDPOINTS.analyzeFile, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        prompt,
        fileRef,
        modelId: request.modelId,
        requestType: 'validation_rule_generation'
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    console.log('Full API response:', data);
    console.log('AI result type:', typeof data.result);
    console.log('AI result content:', data.result);
    
    // Parse the JSON response from the AI
    try {
      // Handle case where result might already be parsed JSON
      let rules;
      if (typeof data.result === 'string') {
        // Try to clean up the response if it has extra text
        let cleanResult = data.result.trim();
        
        // Look for JSON array in the response
        const jsonMatch = cleanResult.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          cleanResult = jsonMatch[0];
        }
        
        console.log('Attempting to parse:', cleanResult);
        rules = JSON.parse(cleanResult);
      } else if (Array.isArray(data.result)) {
        // Result is already parsed
        rules = data.result;
      } else {
        throw new Error('Unexpected result format');
      }
      
      if (!Array.isArray(rules)) {
        console.error('Rules is not an array:', rules);
        throw new Error('AI response is not an array of rules');
      }
      
      // Convert KPI names back to codes for storage in database
      const processedRules = rules.map(rule => ({
        ...rule,
        kpiCodes: Array.isArray(rule.kpiCodes) 
          ? rule.kpiCodes.map((kpiName: string) => {
              // Find the code that matches this name
              const matchingCode = kpiCodes.find(code => getKPIName(code) === kpiName);
              return matchingCode || kpiName; // fallback to original if no match
            })
          : rule.kpiCodes
      }));
      
      console.log('Successfully parsed and processed rules:', processedRules);
      console.log('KPI name to code conversions:', processedRules.map(rule => ({
        ruleText: rule.ruleText,
        originalKpiCodes: rule.kpiCodes,
        convertedKpiCodes: Array.isArray(rule.kpiCodes) 
          ? rule.kpiCodes.map((name: string) => `${name} -> ${kpiCodes.find(code => getKPIName(code) === name) || name}`)
          : []
      })));
      
      return processedRules;
    } catch (parseError: any) {
      console.error('Error parsing AI response:', parseError);
      console.error('Raw response:', data.result);
      console.error('Response type:', typeof data.result);
      throw new Error(`Failed to parse AI response as JSON: ${parseError.message || parseError}`);
    }
  } catch (error) {
    console.error('Error generating validation rules:', error);
    throw error;
  }
}

// Analyze data using Bedrock
export async function analyzeData(request: AnalysisRequest): Promise<string> {
  try {
    const response = await fetch(BEDROCK_ENDPOINTS.analyze, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Analysis failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.result;
  } catch (error: any) {
    console.error('Error during Bedrock analysis:', error);
    throw new Error(`Analysis failed: ${error.message}`);
  }
} 